import type { ApiBody, ApiData } from '@app/api/typed';

export type TenantConfig = ApiData<'/api/v1/tenant'>;
export type TenantUpdate = ApiBody<'/api/v1/tenant', 'put'>;
// O `MiddlewareTestResult` saiu no S-e (2), com a rota que o tipava. O resultado de
// um teste de conexão hoje é `SiteConnectionTest`, em `Sites/SitesModel.ts`.
export type DiscoveryConfig = ApiData<'/api/v1/tenant/discovery'>;
export type DiscoveryConfigUpdate = ApiBody<'/api/v1/tenant/discovery', 'put'>;

// --- cadastro de ambientes (Lote 3 · F1-c) ---------------------------------
//
// Derivados do contrato, como todo o resto deste arquivo: `Environment` deixou de
// ter enum no F1-b, entao `slug` e `string`. O TS nao pode mais garantir o
// vocabulario em tempo de compilacao — quem garante e o servidor, contra o
// cadastro do tenant, e a tela oferece so o que a lista traz.
export type EnvironmentList = ApiData<'/api/v1/tenant/environments'>;
export type Environment = EnvironmentList['items'][number];
export type EnvironmentInput = ApiBody<'/api/v1/tenant/environments', 'post'>;

export type IdentityRole = ApiData<'/api/v1/identity-roles'>[number];
export type IdentityRoleUpdate = ApiBody<'/api/v1/identity-roles/{id}', 'put'>;
export type CanalHumanSubject = NonNullable<TenantConfig['ivr_channel']>;

/**
 * O que o card **Middleware** edita (T21, FR-016). Os demais campos do tenant
 * (cliente, rótulo, trust domain, canais) são dos cards da Story 14 — mas o
 * `updateTenant` os exige, então `paraUpdate` os repassa **como vieram**.
 */
/**
 * O CARD «MIDDLEWARE» SAIU DE T21 NO S-a, e com ele estes tipos.
 *
 * A ponte com o executor é do SITE: endereço e credenciais moram na linha dele, e
 * quem os edita é a seção «Conexão» do drawer da tela «Sites». Enquanto viveram
 * aqui, uma instalação com dois clusters editava «o middleware» como se fosse um
 * só — e o que a pessoa gravava (tenant) e o que o produto usava (site) eram
 * lugares diferentes, com o boot copiando um para o outro a cada subida.
 *
 * `middlewareConfigured` também foi: «não configurado» (FR-119) é uma frase sobre
 * o site, e quem a responde agora é o chip da topbar e o card «Conexão» de T3, os
 * dois lendo o site padrão.
 */

/**
 * Corpo **inteiro** de `updateTenant` a partir do tenant lido — o PUT substitui o
 * documento, e cada card sobrescreve só a sua fatia por cima disto (FR-124: a
 * `version` lida vai junto; 409 `versao_antiga` recarrega).
 */
export function bodyBase(tenant: TenantConfig): TenantUpdate {
  return {
    version: tenant.version,
    client: tenant.client,
    label: tenant.label ?? 'homologacao',
    ivr_channel: channelOrEmpty(tenant.ivr_channel),
    sso_channel: channelOrEmpty(tenant.sso_channel),
  };
}

// ---------------------------------------------------------------- card Tenant (T425)

export type TenantLabel = TenantConfig['label'];

/**
 * Card **Tenant**: Cliente e Rótulo (FR-114).
 *
 * O **Trust domain** saiu no S-b (d): ele tem geografia — é de `site.trust_domain`
 * que sai todo `spiffe://<td>/…` previsto —, e quem o edita é o drawer de «Sites».
 * Enquanto o campo esteve aqui, editá-lo gravava uma coluna que já não compunha ID
 * nenhum: a tela dizia «salvo» e nada mudava.
 *
 * O aviso de divergência (R19) e o `trust_domain_nao_suportado` foram junto — o
 * primeiro é o terceiro bloco do «Testar conexão», o segundo vem no `message` do
 * Salvar do site.
 */
export interface TenantFormValues {
  cliente: string;
  label: TenantLabel;
}

export function tenantDefaults(tenant?: TenantConfig): TenantFormValues {
  return {
    cliente: tenant?.client ?? '',
    label: tenant?.label ?? 'homologacao',
  };
}

export function forUpdateTenant(tenant: TenantConfig, valores: TenantFormValues): TenantUpdate {
  return {
    ...bodyBase(tenant),
    client: valores.cliente.trim(),
    label: valores.label,
  };
}

// O `trustDomainNotSupported` saiu com o campo (S-b (d)) e deixou de existir em
// 15/09: o produto não escreve mais o trust domain em executor nenhum — ele é
// decidido na instalação do cluster, e o cadastro do site adota o valor reportado.

// ---------------------------------------------------------------- canais (T426)

/** Card **Canais do Human Subject** (FR-116): URA · cliente e SSO · funcionário. */
export interface ChannelsFormValues {
  uraIssuer: string;
  uraJwksUrl: string;
  uraMapeamento: string;
  ssoIssuer: string;
  ssoMapeamento: string;
}

export function channelsDefaults(tenant?: TenantConfig): ChannelsFormValues {
  return {
    uraIssuer: tenant?.ivr_channel?.issuer ?? '',
    uraJwksUrl: tenant?.ivr_channel?.jwks_url ?? '',
    uraMapeamento: tenant?.ivr_channel?.subject_mapping ?? '',
    ssoIssuer: tenant?.sso_channel?.issuer ?? '',
    ssoMapeamento: tenant?.sso_channel?.subject_mapping ?? '',
  };
}

export function forUpdateChannels(tenant: TenantConfig, valores: ChannelsFormValues): TenantUpdate {
  return {
    ...bodyBase(tenant),
    ivr_channel: {
      issuer: textOrNull(valores.uraIssuer),
      jwks_url: textOrNull(valores.uraJwksUrl),
      subject_mapping: textOrNull(valores.uraMapeamento),
    },
    // O SSO não tem JWKS no bloco do data-model — vai nulo, sempre.
    sso_channel: {
      issuer: textOrNull(valores.ssoIssuer),
      jwks_url: null,
      subject_mapping: textOrNull(valores.ssoMapeamento),
    },
  };
}

// ---------------------------------------------------------------- discovery (T426)

export interface DiscoveryFormValues {
  ativo: boolean;
  intervaloMin: number;
}

export function discoveryDefaults(config?: DiscoveryConfig): DiscoveryFormValues {
  return {
    ativo: config?.active ?? true,
    intervaloMin: config?.interval_min ?? 5,
  };
}

export function forUpdateDiscovery(valores: DiscoveryFormValues): DiscoveryConfigUpdate {
  return { active: valores.ativo, interval_min: valores.intervaloMin };
}

// ---------------------------------------------------------------- auxiliares

function textOrNull(valor: string): string | null {
  const clean = valor.trim();
  return clean ? clean : null;
}

function channelOrEmpty(canal: TenantConfig['ivr_channel']): CanalHumanSubject {
  return {
    issuer: canal?.issuer ?? null,
    jwks_url: canal?.jwks_url ?? null,
    subject_mapping: canal?.subject_mapping ?? null,
  };
}
